import { useState, useCallback } from 'react';
import { message } from 'antd';
import axios from 'axios';

/**
 * Props for useTransformation hook
 */
interface UseTransformationProps {
  /** Name of the data collection to transform */
  dataCollName: string | undefined;
  /** Client authentication information */
  client: { id: string; token: string };
  /** Callback when transformation status changes */
  onStatusChange?: (status: any) => void;
  /** Function to trigger validation before transformation (expert mode) */
  triggerValidationRefresh?: () => Promise<any>;
  /** Function to show/hide recalculation button */
  setShowRecalc?: (show: boolean) => void;
}

/**
 * useTransformation Hook
 *
 * Triggers data collection transformations. Does NOT poll status.
 *
 * Records trigger timestamp BEFORE API call for accurate stale detection.
 * Backend's `created_at` is compared against this timestamp.
 *
 * @returns Transformation state and trigger functions
 */
export const useTransformation = ({
  dataCollName,
  client,
  onStatusChange,
  triggerValidationRefresh,
  setShowRecalc,
}: UseTransformationProps) => {
  const [loading, setLoading] = useState(false);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [transformationTriggered, setTransformationTriggered] = useState(false);
  const [transformationTriggeredAt, setTransformationTriggeredAt] = useState<Date | null>(null);

  const clearTransformationStatus = useCallback(() => {
    setTransformationTriggered(false);
    setTransformationTriggeredAt(null);
    if (onStatusChange) onStatusChange(null);
  }, [onStatusChange]);

  const transformBasis = useCallback(
    (dataCollBasisName: string) => {
      if (dataCollBasisName.includes('_upload')) {
        setLoading(true);
      } else {
        setLegacyLoading(true);
      }

      const triggerTime = new Date();

      return axios
        .post(
          `${process.env.REACT_APP_API_URL}/b2zero/${client.id}/create_data_coll`,
          { dataCollBasisName },
          {
            headers: {
              'Authorization': `Bearer ${client.token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        .then(() => {
          message.success('Transformation erfolgreich angestoßen', 6);
          setTransformationTriggered(true);
          setTransformationTriggeredAt(triggerTime);
        })
        .catch((error) => {
          message.error('Fehler bei der Transformation');
          throw error;
        })
        .finally(() => {
          if (dataCollBasisName.includes('_upload')) {
            setLoading(false);
          } else {
            setLegacyLoading(false);
          }
        });
    },
    [client.id, client.token]
  );

  const handleGenerate = useCallback(() => {
    if (!dataCollName) {
      return Promise.reject(new Error('No data collection selected'));
    }

    if (!triggerValidationRefresh || !setShowRecalc) {
      return transformBasis(dataCollName + '_basis_upload');
    }

    setShowRecalc(false);
    return triggerValidationRefresh().then((result: any) => {
      if (!result?.valid) {
        throw new Error('Validation failed');
      }
      return transformBasis(dataCollName + '_basis_upload');
    });
  }, [dataCollName, transformBasis, triggerValidationRefresh, setShowRecalc]);

  const handleGenerateLegacyDataColl = useCallback(() => {
    if (!dataCollName) {
      return Promise.reject(new Error('No data collection selected'));
    }

    if (setShowRecalc) {
      setShowRecalc(false);
    }
    return transformBasis(dataCollName + '_basis');
  }, [dataCollName, transformBasis, setShowRecalc]);

  return {
    loading,
    legacyLoading,
    transformationTriggered,
    transformationTriggeredAt,
    transformBasis,
    handleGenerate,
    handleGenerateLegacyDataColl,
    clearTransformationStatus,
  };
};
